import { observable, action, makeAutoObservable, runInAction, computed, toJS } from "mobx";
import { Conversation, DocumentsFilters, modelNames, views } from "./types";
import axios from "axios";

class Store {
  @observable model: modelNames = modelNames.documentModelName;
  @observable conversations: Conversation[] = [];
  @observable userName: string | null = null;
  @observable userEmail: string | null = null;
  @observable documentsSelected: string[] = [];
  @observable filtersApplied = false;
  @observable previousConversation: Conversation = {
    ClientID: this.userName,
    ConversationID: null,
    ModelType: this.model,
    Content: [],
    Summary: "",
    Filters: {
      case_laws: {
        laws: [],
        sections: [],
        keywords: [],
      },
      tax_guidances: {
        tax_guidances: [],
        tax_categories: [],
        keywords: [],
      },
      client_work: {
        document_classes: [],
        related_laws: [],
        keywords: [],
      },
    },
    TimeStamp: "",
    agent_mode: null,
    SelectedDocuments: [],
  };
  @observable selectedConversation: Conversation = {
    ClientID: this.userName,
    ConversationID: null,
    ModelType: this.model,
    Content: [],
    Summary: "",
    Filters: {
      case_laws: {
        laws: [],
        sections: [],
        keywords: [],
      },
      tax_guidances: {
        tax_guidances: [],
        tax_categories: [],
        keywords: [],
      },
      client_work: {
        document_classes: [],
        related_laws: [],
        keywords: [],
      },
    },
    TimeStamp: "",
    agent_mode: null,
    SelectedDocuments: [],
  };
  @observable view: views = views.chat;
  @observable goback: boolean = false;
  @observable desktop: boolean = false;
  @observable bearerToken: string | null = null;
  @observable properties: DocumentsFilters | undefined;
  @observable filters: DocumentsFilters = {
    case_laws: {
      laws: [],
      sections: [],
      keywords: [],
    },
    tax_guidances: {
      tax_guidances: [],
      tax_categories: [],
      keywords: [],
    },
    client_work: {
      document_classes: [],
      related_laws: [],
      keywords: [],
    },
  };
  @observable law_section_mapping?: {};
  @observable hideIntro: boolean = false;
  @computed numFilters: number;
  @observable documents: DocumentsFilters = {
    case_laws: {
      laws: [],
      sections: [],
      keywords: [],
    },
    tax_guidances: {
      tax_guidances: [],
      tax_categories: [],
      keywords: [],
    },
    client_work: {
      document_classes: [],
      related_laws: [],
      keywords: [],
    },
  };
  constructor() {
    makeAutoObservable(
      this,
      {
        model: true,
        conversations: true,
        previousConversation: true,
        selectedConversation: true,
        userName: true,
        desktop: true,
        goback: true,
        bearerToken: true,
        properties: true,
        documents: true,
        documentsSelected: true,
        filters: true,
        numFilters: true,
        userEmail: true,
      },
      {
        autoBind: true,
      }
    );

    this.getPrevious();
    this.getModel();
    this.getConversations();
    this.getClientID();
    this.getPlatform();
    this.getToken();
    this.getFilters();
    this.getNumFilters();
    this.getHideIntro();
    this.getEmail();
  }

  @action setModel(model: modelNames) {
    this.model = model;
    runInAction(() => (this.model = model));
  }

  @action getModel() {
    return this.model;
  }

  @action setFiltersApplied(value: boolean) {
    this.filtersApplied = value;
  }

  @action setToken(token: string) {
    this.bearerToken = token;
    runInAction(() => (this.bearerToken = token));
  }

  @action getToken() {
    return this.bearerToken;
  }

  @action setGoback(back: boolean) {
    this.goback = back;
    runInAction(() => (this.goback = back));
  }

  @action getGoBack() {
    return this.goback;
  }
  // In your MobX store (store.tsx)
  @action async fetchProperties() {
    if (this.bearerToken?.length > 0 && !this.properties) {
      const response = await axios.get("https://kafka-legal-analyst-be-stg.pwc.fi/get_properties", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.bearerToken}`,
        },
      });
      const data = await response.data;
      if (!data.error) {
        runInAction(() => {
          this.setProperties(data);
        });
      }
    }
  }

  @action async fetchFilteredDocuments() {
    if (!this.bearerToken || !this.filters) return;

    const response = await axios.post(
      "https://kafka-legal-analyst-be-stg.pwc.fi/get_filtered_documents",
      this.filters, // body
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.bearerToken}`,
        },
      }
    );

    const data = response.data;
  
    if (!data.error) {
      runInAction(() => {
        this.setDocuments(data);
      });
    }
  }
  @action setPrevious(convos: Conversation) {
    this.previousConversation = convos;
    runInAction(() => (this.previousConversation = convos));
  }

  @action getPrevious() {
    return toJS(this.previousConversation);
  }

  @action setUsername(userName_: string) {
    this.userName = userName_;
    runInAction(() => (this.userName = userName_));
  }

  @action getUsername() {
    return this.userName;
  }

  @action getClientID() {
    return this.model == modelNames.baseModelName ? this.userEmail : this.userEmail + "_docs";
  }

  @action setConversations(conversations: Conversation[]) {
    this.conversations = conversations;
    runInAction(() => (this.conversations = conversations));
  }

  @action getConversations() {
    return this.conversations;
  }

  @action setSelectedConversation(selectedConv_: Conversation) {
    this.selectedConversation = selectedConv_;
    runInAction(() => (this.selectedConversation = selectedConv_));
  }
  @action setDocuments(documents_: DocumentsFilters) {
    this.documents = documents_;
    runInAction(() => (this.documents = documents_));
  }

  @action getSelectedConversation() {
    return toJS(this.selectedConversation);
  }

  @action getView() {
    return this.view;
  }

  @action setView(view_: views) {
    this.view = view_;
    runInAction(() => (this.view = view_));
  }

  @action getPlatform() {
    return this.desktop;
  }

  @action setPlatform(platform_: boolean) {
    this.desktop = platform_;
    runInAction(() => (this.desktop = platform_));
  }

  @action setProperties(props_: DocumentsFilters) {
    this.properties = props_;
    runInAction(() => (this.properties = props_));
  }

  @action getProperties() {
    return this.properties;
  }

  @action getDocuments() {
    return this.documents;
  }
  @action setDocumentsSelected(documents_: string[]) {
    this.documentsSelected = documents_;
    runInAction(() => (this.documentsSelected = documents_));
  }

  @action getDocumentsSelected() {
    return this.documentsSelected;
  }

  @action setFilters(newFilters: DocumentsFilters) {
    runInAction(() => {
      Object.assign(this.filters, newFilters);
    });
  }

  @action getFilters() {
    return this.filters;
  }

  @action resetFilters() {
    this.filters = {
      case_laws: {
        laws: [],
        sections: [],
        keywords: [],
      },
      tax_guidances: {
        tax_guidances: [],
        tax_categories: [],
        keywords: [],
      },
      client_work: {
        document_classes: [],
        related_laws: [],
        keywords: [],
      },
    };
    runInAction(
      () =>
        (this.filters = {
          case_laws: {
            laws: [],
            sections: [],
            keywords: [],
          },
          tax_guidances: {
            tax_guidances: [],
            tax_categories: [],
            keywords: [],
          },
          client_work: {
            document_classes: [],
            related_laws: [],
            keywords: [],
          },
        })
    );
  }

  @computed getNumFilters(): number {
    return (
      this.filters.case_laws.laws.length +
      this.filters.case_laws.sections.length +
      this.filters.case_laws.keywords.length +
      this.filters.tax_guidances.tax_guidances.length +
      this.filters.tax_guidances.tax_categories.length +
      this.filters.tax_guidances.keywords.length +
      this.filters.client_work.document_classes.length +
      this.filters.client_work.related_laws.length +
      this.filters.client_work.keywords.length
    );
  }

  @action setHideIntro(v: boolean) {
    this.hideIntro = v;
    runInAction(() => (this.hideIntro = v));
  }

  @action getHideIntro(): boolean {
    return this.hideIntro;
  }

  @action setEmail(v: string) {
    this.userEmail = v;
    runInAction(() => (this.userEmail = v));
  }

  @action getEmail() {
    return this.userEmail;
  }
}

export default Store;
